import React, { useState } from "react";
import PropTypes from "prop-types";

function NormsBarGraph({
  label,
  min,
  max,
  normalMin,
  normalMax,
  currentValue,
  phantomValue, // optional prop for phantom marker
  unit = "",
  showPhantom, // boolean from parent: if true, phantom marker is shown permanently
}) {
  // Local state for hover over the entire component (row)
  const [isHovered, setIsHovered] = useState(false);
  // Effective flag: show phantom marker if parent's showPhantom is true OR if the component is hovered
  const effectiveShowPhantom = showPhantom || isHovered;

  // Constrain values so the main marker stays within [min, max]
  const safeCurrent = Math.min(Math.max(currentValue, min), max);
  const safeNormalMin = Math.min(Math.max(normalMin, min), max);
  const safeNormalMax = Math.min(Math.max(normalMax, min), max);

  // Calculate total range and percentages for positioning
  const totalRange = max - min;
  const normalStartPct = ((safeNormalMin - min) / totalRange) * 100;
  const normalWidthPct = ((safeNormalMax - safeNormalMin) / totalRange) * 100;
  const currentPct = ((safeCurrent - min) / totalRange) * 100;

  // For phantom marker, if provided, clamp it and calculate its position
  let safePhantom = null;
  let phantomPct = null;
  if (phantomValue !== undefined && phantomValue !== null) {
    safePhantom = Math.min(Math.max(phantomValue, min), max);
    phantomPct = ((safePhantom - min) / totalRange) * 100;
  }

  // Fixed width values for consistent layout
  const labelWidth = 60; // fixed width for min/max labels to ensure alignment
  const markerWidth = 7;
  const markerHeight = 16;
  const barHeight = 10; // Height of the bar

  // Determine the normal range highlight color
  const isInNormalRange = currentValue >= normalMin && currentValue <= normalMax;
  const normalRangeColor = isInNormalRange ? "var(--bs-primary)" : "#b0b0b0";

  return (
    <div
      className="mb-4"
      style={{ width: "100%" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Title Row - slightly offset above */}
      {label && (
        <div className="row">
          <div className="col" style={{ textAlign: "left" }}>
            <span className="fw-bold">{label}:</span>
          </div>
        </div>
      )}
      
      {/* Graph Row - content takes up 90% width, aligned to the right */}
      <div className="row">
        <div className="col" style={{ paddingRight: "0" }}>
          <div style={{ width: "97%", marginLeft: "auto" }}>
            <div style={{ display: "flex", position: "relative", height: "40px" }}>
              {/* Min Label */}
              <div style={{ 
                width: `${labelWidth}px`,
                textAlign: "right",
                paddingRight: "8px",
                position: "absolute",
                left: 0,
                bottom: 0, // Align to bottom where the bar is
                height: `${barHeight}px` // Same height as the bar
              }}>
                <span style={{ 
                  fontSize: "0.8rem",
                  lineHeight: `${barHeight}px`, // Exactly match the bar height
                  display: "block"
                }}>
                  {min}
                  {unit}
                </span>
              </div>
              
              {/* Graph - taking most of the space */}
              <div style={{ 
                position: "relative", 
                flex: "1 1 auto", 
                marginLeft: `${labelWidth}px`,
                marginRight: `${labelWidth}px`
              }}>
                {/* Full Range Bar */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#e0e0e0",
                    height: `${barHeight}px`,
                    borderRadius: "5px",
                    width: "100%"
                  }}
                >
                  {/* Normal Range Highlight */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${normalStartPct}%`,
                      width: `${normalWidthPct}%`,
                      backgroundColor: normalRangeColor,
                      borderRadius: "5px",
                    }}
                  ></div>
                </div>
                {/* Main Marker with Label (above the bar) */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "-3px",
                    left: `${currentPct}%`,
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      marginBottom: "2px",
                    }}
                  >
                    {currentValue !== null && currentValue !== undefined && !isNaN(currentValue) ? `${currentValue}${unit}` : "N/A"}
                  </div>
                  <div
                    style={{
                      width: `${markerWidth}px`,
                      height: `${markerHeight}px`,
                      backgroundColor: "#000",
                      borderRadius: "3px",
                    }}
                  ></div>
                </div>
                {/* Phantom Marker with Label (below the bar), shown if effectiveShowPhantom is true */}
                {safePhantom !== null && phantomPct !== null && effectiveShowPhantom && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-24px",
                      left: `${phantomPct}%`,
                      transform: "translateX(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      opacity: 0.8,
                    }}
                  >
                    <div
                      style={{
                        width: `${markerWidth}px`,
                        height: `${markerHeight}px`,
                        backgroundImage: "repeating-linear-gradient(10deg, #000, #000 2px, transparent 2px, transparent 4px)",
                        borderRadius: "3px",
                        border: "1px solid #000",
                      }}
                    ></div>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        marginTop: "2px",
                      }}
                    >
                      {phantomValue !== null && phantomValue !== undefined && !isNaN(phantomValue) ? `${phantomValue}${unit}` : "N/A"}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Max Label */}
              <div style={{ 
                width: `${labelWidth}px`,
                textAlign: "left",
                paddingLeft: "8px",
                position: "absolute",
                right: 0,
                bottom: 0, // Align to bottom where the bar is
                height: `${barHeight}px` // Same height as the bar
              }}>
                <span style={{ 
                  fontSize: "0.8rem",
                  lineHeight: `${barHeight}px`, // Exactly match the bar height
                  display: "block"
                }}>
                  {max}
                  {unit}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

NormsBarGraph.propTypes = {
  label: PropTypes.string,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  normalMin: PropTypes.number.isRequired,
  normalMax: PropTypes.number.isRequired,
  currentValue: PropTypes.number.isRequired,
  phantomValue: PropTypes.number,
  unit: PropTypes.string,
  showPhantom: PropTypes.bool,
};

export default NormsBarGraph;